import { QueryClient } from "@tanstack/react-query";
import { ApiError } from "./api";
import { useScopeStore } from "@/stores/scope";

/** Default TanStack Query config.
 *
 *   - 30 s stale time: most coach surfaces tolerate slightly stale
 *     reads between active actions; the per-query `staleTime` override
 *     is fine for fast-moving surfaces (admin performance, live status).
 *   - retry: skip 4xx (auth / not-found / validation are user errors,
 *     retrying would just hammer the API). Network/5xx get one retry.
 *   - refetchOnWindowFocus: enabled — focusing the tab is the cheapest
 *     "is this still fresh?" signal we get.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 1;
      },
      refetchOnWindowFocus: true,
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Scope-aware query key helpers.
 *
 * Convention: every scoped query key is a 3-element tuple
 *   [domain, teamId-or-null, seasonId-or-null, ...rest]
 * so that switching scope auto-invalidates the cache for everything in
 * the previous scope (TanStack Query treats key tuples as structurally
 * equal — different tuples are different caches).
 *
 * Two flavors:
 *   - `useScopedKey("domain", extra?)` — React HOOK. Subscribes to
 *     both `teamId` and `seasonId` from the scope store via Zustand
 *     selectors, so the component re-renders whenever either changes.
 *     ALWAYS use this inside `useQuery({ queryKey: useScopedKey(...) })`.
 *     The `use` prefix marks it as a hook so the react-hooks ESLint
 *     rule (when we add it) will catch misuse — and so a non-React
 *     caller has an obviously wrong-named function to reach for.
 *   - `scopedKeyOf(scope, "domain", extra?)` — non-hook snapshot.
 *     For mutation callbacks, invalidation predicates, and anywhere a
 *     hook call would throw "Invalid hook call".
 *
 * Examples:
 *   queryKey: useScopedKey("matches")
 *     → ["matches", "team-riverside", "season-riverside-2026"]
 *   queryKey: useScopedKey("coaching-notes", { match_id: "..." })
 *     → ["coaching-notes", "team-…", "season-…", { match_id: "..." }]
 *   queryKey: unscopedKey("settings-public")
 *     → ["settings-public"]
 *
 * For invalidation across all queries in the current scope after a
 * mutation, prefer `invalidateScope()` over per-key invalidation —
 * cheaper to maintain as the surface grows.
 */

export type ScopedQueryKey =
  | readonly [domain: string, teamId: string | null, seasonId: string | null]
  | readonly [
      domain: string,
      teamId: string | null,
      seasonId: string | null,
      extra: Record<string, unknown>,
    ];

export type UnscopedQueryKey = readonly [domain: string, extra?: Record<string, unknown>];

/** Build a scope-aware key tuple from the CURRENT scope (REACT HOOK).
 *
 * Must be called from inside a React render. Subscribes to BOTH
 * `teamId` and `seasonId` via Zustand selectors so the consuming
 * component re-renders whenever either changes — a previous version
 * called `useScopeStore.getState()` here (unsubscribed read), which
 * meant a season swap within the same team would NOT advance the
 * query key.
 *
 * For non-React callers (mutation onSuccess, invalidation predicates,
 * any context where a hook call would throw "Invalid hook call"),
 * use `scopedKeyOf({ teamId, seasonId }, "domain")` instead.
 */
export function useScopedKey(
  domain: string,
  extra?: Record<string, unknown>,
): ScopedQueryKey {
  const teamId = useScopeStore((s) => s.teamId);
  const seasonId = useScopeStore((s) => s.seasonId);
  if (extra && Object.keys(extra).length > 0) {
    return [domain, teamId, seasonId, extra] as const;
  }
  return [domain, teamId, seasonId] as const;
}

/** Non-hook variant for code outside a React render. Pass an explicit
 * scope snapshot (typically from `useScopeStore.getState()`). */
export function scopedKeyOf(
  scope: { teamId: string | null; seasonId: string | null },
  domain: string,
  extra?: Record<string, unknown>,
): ScopedQueryKey {
  if (extra && Object.keys(extra).length > 0) {
    return [domain, scope.teamId, scope.seasonId, extra] as const;
  }
  return [domain, scope.teamId, scope.seasonId] as const;
}

/** Build an unscoped key — for /api/me, /api/settings/public, admin
 * routes, etc. */
export function unscopedKey(
  domain: string,
  extra?: Record<string, unknown>,
): UnscopedQueryKey {
  return extra && Object.keys(extra).length > 0
    ? ([domain, extra] as const)
    : ([domain] as const);
}

/** Invalidate every scoped query in the current scope. Use after
 * mutations that may touch multiple domain caches (e.g. saving a
 * scope switch).
 *
 * Match is structural — any tuple whose 2nd/3rd elements match the
 * current scope is in scope for invalidation. `invalidateQueries`
 * marks the data stale and triggers a refetch on next observe —
 * appropriate for "we just changed state" freshness, but NOT for
 * privacy isolation (the old data stays in the cache and is returned
 * synchronously while the refetch is in flight). For the auth
 * boundary, use `removeAuthenticatedFromCache()` instead.
 */
export async function invalidateScope(): Promise<void> {
  const { teamId, seasonId } = useScopeStore.getState();
  await queryClient.invalidateQueries({
    predicate: (q) => {
      const key = q.queryKey as unknown[];
      if (key.length < 3) return false;
      return key[1] === teamId && key[2] === seasonId;
    },
  });
}

/** Evict EVERY scoped query from the cache, regardless of scope.
 *
 * **Use this on auth boundaries (sign-out, 401 from /api/me)** rather
 * than `invalidateQueries`. The difference is privacy-shaped:
 *
 *   - `invalidateQueries` flags entries as stale → background refetch,
 *     but the cached data is still returned synchronously to any
 *     observer that subscribes in the meantime. After a logout +
 *     re-login in the same tab where the next user has the SAME
 *     `[domain, teamId, seasonId]` tuple (common: shared lab machine,
 *     family laptop), the new user can momentarily see the previous
 *     user's coach/admin data while the refetch is in flight.
 *   - `removeQueries` evicts entries entirely → subscribers go into
 *     `pending` state and have to wait for the new fetch. No stale
 *     data is ever served across the auth boundary.
 *
 * Invalidation is for freshness; removal is for privacy isolation.
 */
export function removeAllScopedFromCache(): void {
  queryClient.removeQueries({
    predicate: (q) => (q.queryKey as unknown[]).length >= 3,
  });
}

/** Unscoped domains that are safe to keep across sign-out because they
 * are public-anonymous data already rendered on public pages. Everything
 * else without a scope tuple is treated as authenticated/private and is
 * evicted on auth boundaries.
 */
const PUBLIC_UNSCOPED_CACHE_DOMAINS = new Set([
  "app-settings",
  "settings-public",
  "public-matches",
  "public-teams",
  "public-team",
  "live-status",
  "live-status-season-cta",
  "team-live-status",
  "live-status-team",
  "public-team-statuses",
]);

/** Evict every authenticated/private query from the cache.
 *
 * This includes all scoped queries AND private unscoped admin/account
 * domains such as `admin-users`, `admin-settings`, and `me`. Public
 * season/game/live caches are intentionally retained so signing out from
 * a public page doesn't blank the page; they contain only anonymous-safe
 * data and will still refetch when their normal stale/refocus rules fire.
 */
export function removeAuthenticatedFromCache(): void {
  queryClient.removeQueries({
    predicate: (q) => {
      const key = q.queryKey as unknown[];
      if (key.length >= 3) return true;
      const domain = typeof key[0] === "string" ? key[0] : "";
      return !PUBLIC_UNSCOPED_CACHE_DOMAINS.has(domain);
    },
  });
}
